import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { ChangedOptions, LastContactOptions, QuickFilters } from '../filters/QuickFilters'
import type { FilterOptions } from '../filters/useFilterOptions'
import { normalizeFilter } from '../query/codec'
import type { Filter, OptionDef, Register } from '../query/types'

/** The two quick filters that get a panel of their own here; the others stay buttons. */
const PANELS = ['last_activity_at', 'last_contact_at']

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  )
}

/**
 * The choice for a sort the list has that the choices do not (a column header on desktop, a shared
 * link), named after its property: "E-mail (aflopend)". Without it the select would show the first
 * choice for a list that is not sorted that way, and picking that one would be no change.
 */
function currentSortOption(sort: string, register: Register): OptionDef {
  const property = register.properties.find((candidate) => candidate.key === sort.replace(/^-/, ''))
  const direction = sort.startsWith('-') ? 'aflopend' : 'oplopend'
  return { value: sort, label: property ? `${property.label} (${direction})` : 'Onbekend' }
}

/**
 * Sort and filters on a phone (design §4.2, ED-60): below 768 px the filter bar keeps only the
 * search field and the count, and this sheet holds the rest: the sort, "Gewijzigd" and "Laatste
 * contact" open as panels, the other quick filters, and the way to the advanced filters. It writes
 * through at once; "Toon n contacten" only closes it.
 */
export function MobileFilterSheet({
  open,
  onOpenChange,
  register,
  filter,
  sort,
  sortOptions,
  options,
  today,
  total,
  noun,
  onFilterChange,
  onSortChange,
  onAdvanced,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  register: Register
  filter: Filter
  /** The sort the list has now, e.g. "name" or "-last_activity_at". */
  sort: string
  sortOptions: readonly OptionDef[]
  options: FilterOptions
  /** Today in Amsterdam as YYYY-MM-DD. */
  today: string
  /** How many records match; null while that is not known. */
  total: number | null
  /** "9 contacten", "1 contact". */
  noun: (count: number) => string
  onFilterChange: (next: Filter) => void
  onSortChange: (sort: string) => void
  onAdvanced: () => void
}) {
  const panel = (key: string) => (register.quick_filters.includes(key) ? register.properties.find((property) => property.key === key) : undefined)
  const changed = panel('last_activity_at')
  const contact = panel('last_contact_at')
  const author = register.properties.find((property) => property.key === 'last_activity_by')
  const users = (author && options.optionsFor(author)) ?? []
  const others = register.quick_filters.some((key) => !PANELS.includes(key) && register.properties.some((property) => property.key === key))
  const sorts = sortOptions.some((choice) => choice.value === sort) ? sortOptions : [...sortOptions, currentSortOption(sort, register)]
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="md" aria-describedby={undefined}>
        <SheetTitle className="text-lg">Filters en sortering</SheetTitle>
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          <Section title="Sorteren">
            <select
              aria-label="Sorteren"
              value={sort}
              onChange={(event) => onSortChange(event.target.value)}
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-base"
            >
              {sorts.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </Section>
          {changed ? (
            <Section title="Gewijzigd">
              <ChangedOptions filter={filter} today={today} help={changed.help} users={users} onChange={onFilterChange} />
            </Section>
          ) : null}
          {contact ? (
            <Section title="Laatste contact">
              <LastContactOptions filter={filter} today={today} help={contact.help} onChange={onFilterChange} />
            </Section>
          ) : null}
          {others ? (
            <Section title="Meer filters">
              <QuickFilters register={register} filter={filter} options={options} today={today} onChange={onFilterChange} except={PANELS} />
            </Section>
          ) : null}
          <Button type="button" variant="outline" className="self-start" onClick={onAdvanced}>
            Geavanceerd
          </Button>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          {/* Two panels above have a "Wissen" of their own; this one takes every filter away. */}
          <Button type="button" variant="ghost" aria-label="Alle filters wissen" onClick={() => onFilterChange(normalizeFilter({}))}>
            Wissen
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {total === null ? 'Toon resultaten' : `Toon ${noun(total)}`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
